import { FormEvent, useEffect, useMemo, useState } from "react";
import { api, API_URL } from "@/lib/api";
import { ExtensionSelect, type PbxExtensionOption } from "@/components/ExtensionSelect";
import { useSession } from "@/lib/session";
import { localNumberInput, normalizeLocalNumber } from "@/lib/shared";

type Group = {
  id: string;
  name: string;
  description: string;
  contactCount: number;
};

type Contact = {
  id: string;
  groupId: string;
  name: string;
  phone: string;
  variables: Record<string, string>;
  notes: string;
};

type Prompt = {
  id: string;
  name: string;
  kind: "file" | "url" | "script";
  fileRef: string;
  scriptTemplate: string;
  notes: string;
};

type Campaign = {
  id: string;
  groupId: string | null;
  promptId: string | null;
  name: string;
  mode: "live" | "prerecorded";
  extension: string;
  autoAnswer: boolean;
  delaySec: number;
  status: string;
  total: number;
  queued: number;
  dialed: number;
  answered: number;
  failed: number;
  skipped: number;
  startedAt?: string | null;
  finishedAt?: string | null;
};

type CampaignDetail = Campaign & {
  items: Array<{
    id: string;
    phone: string;
    displayName: string;
    status: string;
    renderedScript: string;
    errorMessage: string;
    dialedAt?: string | null;
    endedAt?: string | null;
  }>;
};

type Tab = "groups" | "prompts" | "launch" | "campaigns";

export default function BulkCallingPage() {
  const { user } = useSession();
  const [tab, setTab] = useState<Tab>("groups");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);

  const [groups, setGroups] = useState<Group[]>([]);
  const [activeGroupId, setActiveGroupId] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [activeCampaign, setActiveCampaign] = useState<CampaignDetail | null>(null);
  const [extensions, setExtensions] = useState<PbxExtensionOption[]>([]);

  const [groupName, setGroupName] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [numbersModalOpen, setNumbersModalOpen] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalOk, setModalOk] = useState("");

  const [promptName, setPromptName] = useState("");
  const [promptKind, setPromptKind] = useState<Prompt["kind"]>("file");
  const [promptFile, setPromptFile] = useState("");
  const [promptScript, setPromptScript] = useState(
    "Hello {{name}}, this is an automated message regarding your account.",
  );
  const [promptPreview, setPromptPreview] = useState("");

  const [launchMode, setLaunchMode] = useState<"live" | "prerecorded">("live");
  const [launchScope, setLaunchScope] = useState<"all" | "selected">("all");
  const [launchExt, setLaunchExt] = useState(user?.extension || "");
  const [launchPromptId, setLaunchPromptId] = useState("");
  const [launchDelay, setLaunchDelay] = useState(45);
  const [launchAuto, setLaunchAuto] = useState(true);
  const [launchName, setLaunchName] = useState("");

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected],
  );

  async function refreshGroups() {
    const data = await api<Group[]>("/bulk/groups");
    setGroups(data || []);
    if (!activeGroupId && data?.[0]) setActiveGroupId(data[0].id);
  }

  async function refreshContacts(groupId = activeGroupId) {
    if (!groupId) {
      setContacts([]);
      return;
    }
    const data = await api<Contact[]>(`/bulk/groups/${groupId}/contacts`);
    setContacts(data || []);
    setSelected({});
  }

  async function refreshPrompts() {
    const data = await api<Prompt[]>("/bulk/prompts");
    setPrompts(data || []);
    if (!launchPromptId && data?.[0]) setLaunchPromptId(data[0].id);
  }

  async function refreshCampaigns() {
    const data = await api<Campaign[]>("/bulk/campaigns");
    setCampaigns(data || []);
  }

  async function loadAll() {
    setBusy(true);
    setError("");
    try {
      await Promise.all([refreshGroups(), refreshPrompts(), refreshCampaigns()]);
      try {
        const ext = await api<PbxExtensionOption[]>("/org/extensions");
        setExtensions(Array.isArray(ext) ? ext : []);
      } catch {
        setExtensions([]);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not load bulk calling";
      setError(`${msg} (API: ${API_URL || "same origin"})`);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void loadAll();
  }, []);

  useEffect(() => {
    if (!ok) return;
    const t = setTimeout(() => setOk(""), 3500);
    return () => clearTimeout(t);
  }, [ok]);

  useEffect(() => {
    if (activeGroupId) void refreshContacts(activeGroupId).catch(() => setContacts([]));
  }, [activeGroupId]);

  const runningCampaignId = useMemo(
    () => campaigns.find((c) => c.status === "running")?.id || "",
    [campaigns],
  );
  const pausedCampaignId = useMemo(
    () => campaigns.find((c) => c.status === "paused")?.id || "",
    [campaigns],
  );

  useEffect(() => {
    const liveId =
      runningCampaignId ||
      pausedCampaignId ||
      (activeCampaign && ["running", "paused"].includes(activeCampaign.status)
        ? activeCampaign.id
        : "");
    if (!liveId) return;

    const tick = () => {
      void refreshCampaigns().catch(() => {});
      void api<CampaignDetail>(`/bulk/campaigns/${liveId}`)
        .then(setActiveCampaign)
        .catch(() => {});
    };

    tick();
    const t = setInterval(tick, 2500);
    return () => clearInterval(t);
  }, [runningCampaignId, pausedCampaignId, activeCampaign?.id, activeCampaign?.status]);

  // Prefer the running campaign in the side panel.
  useEffect(() => {
    if (!runningCampaignId) return;
    if (activeCampaign?.id === runningCampaignId && activeCampaign.status === "running") return;
    void api<CampaignDetail>(`/bulk/campaigns/${runningCampaignId}`)
      .then(setActiveCampaign)
      .catch(() => {});
  }, [runningCampaignId, activeCampaign?.id, activeCampaign?.status]);

  function parseImportLine(line: string): { phone: string; name: string; variables: Record<string, string> } | null {
    const raw = line.trim();
    if (!raw) return null;
    const parts = raw.split(/[,;\t|]/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return null;

    const phonePart = parts.find((p) => /\d{8,}/.test(p.replace(/\D/g, ""))) || parts[parts.length - 1];
    const namePart = parts.find((p) => p !== phonePart && !/^[a-zA-Z0-9_]+=/.test(p)) || "";
    const variables: Record<string, string> = {};
    for (const p of parts) {
      const m = p.match(/^([a-zA-Z0-9_]+)=(.+)$/);
      if (m) variables[m[1]] = m[2].trim();
    }
    if (namePart) variables.name = namePart;
    return { phone: phonePart, name: namePart, variables };
  }

  async function openNumbersModal(groupId: string) {
    setActiveGroupId(groupId);
    setModalError("");
    setModalOk("");
    setContactName("");
    setContactPhone("");
    setPasteText("");
    setShowImport(false);
    setNumbersModalOpen(true);
    setBusy(true);
    try {
      await refreshContacts(groupId);
    } catch {
      setContacts([]);
    } finally {
      setBusy(false);
    }
  }

  async function createGroup(e: FormEvent) {
    e.preventDefault();
    const name = groupName.trim();
    if (!name) return;
    setModalError("");
    setBusy(true);
    try {
      const g = await api<Group>("/bulk/groups", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      setGroupName("");
      setGroupModalOpen(false);
      setOk(`Group “${g.name}” created`);
      await refreshGroups();
      setActiveGroupId(g.id);
      await openNumbersModal(g.id);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Could not create group");
    } finally {
      setBusy(false);
    }
  }

  async function addOneContact(e: FormEvent) {
    e.preventDefault();
    if (!activeGroupId) return;
    const name = contactName.trim();
    const ten = localNumberInput(contactPhone);
    if (ten.length !== 10) {
      setModalError("Enter a valid 10-digit phone number");
      return;
    }
    setModalError("");
    setModalOk("");
    setBusy(true);
    try {
      const dial = normalizeLocalNumber(ten);
      const variables: Record<string, string> = {};
      if (name) variables.name = name;
      const res = await api<{ addedCount: number; errorCount: number; errors: Array<{ error: string }> }>(
        `/bulk/groups/${activeGroupId}/contacts`,
        {
          method: "POST",
          body: JSON.stringify({ name, phone: dial, variables }),
        },
      );
      if (res.errorCount) throw new Error(res.errors?.[0]?.error || "Invalid number");
      setContactName("");
      setContactPhone("");
      setModalOk("Number added");
      await refreshContacts(activeGroupId);
      await refreshGroups();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Could not add number");
    } finally {
      setBusy(false);
    }
  }

  async function importPaste() {
    if (!activeGroupId) return;
    const contactsPayload = pasteText
      .split(/\r?\n/)
      .map(parseImportLine)
      .filter((row): row is NonNullable<typeof row> => Boolean(row));
    if (!contactsPayload.length) {
      setModalError("Paste lines as: Name, Phone");
      return;
    }
    setModalError("");
    setModalOk("");
    setBusy(true);
    try {
      const res = await api<{ addedCount: number; errorCount: number }>(
        `/bulk/groups/${activeGroupId}/contacts`,
        { method: "POST", body: JSON.stringify({ contacts: contactsPayload }) },
      );
      setPasteText("");
      setShowImport(false);
      setModalOk(`Imported ${res.addedCount} number(s)${res.errorCount ? `, ${res.errorCount} skipped` : ""}`);
      await refreshContacts(activeGroupId);
      await refreshGroups();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setBusy(false);
    }
  }

  async function removeContact(id: string) {
    if (!activeGroupId) return;
    setBusy(true);
    setModalError("");
    try {
      await api(`/bulk/groups/${activeGroupId}/contacts/${id}`, { method: "DELETE" });
      await refreshContacts(activeGroupId);
      await refreshGroups();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  async function deleteGroup(id: string) {
    if (!window.confirm("Delete this group and all numbers?")) return;
    setBusy(true);
    try {
      await api(`/bulk/groups/${id}`, { method: "DELETE" });
      if (activeGroupId === id) {
        setActiveGroupId("");
        setContacts([]);
        setNumbersModalOpen(false);
      }
      await refreshGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  async function createPrompt(e: FormEvent) {
    e.preventDefault();
    setError("");
    setOk("");
    setBusy(true);
    try {
      await api("/bulk/prompts", {
        method: "POST",
        body: JSON.stringify({
          name: promptName,
          kind: promptKind,
          fileRef: promptFile,
          scriptTemplate: promptScript,
        }),
      });
      setPromptName("");
      setOk("Voice prompt saved");
      await refreshPrompts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save prompt");
    } finally {
      setBusy(false);
    }
  }

  async function previewPrompt() {
    try {
      const vars = { name: "Rahul", city: "Mumbai", phone: "9876543210" };
      const data = await api<{ rendered: string }>("/bulk/prompts/preview", {
        method: "POST",
        body: JSON.stringify({ scriptTemplate: promptScript, variables: vars }),
      });
      setPromptPreview(data.rendered);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Preview failed");
    }
  }

  async function launchCampaign(e: FormEvent) {
    e.preventDefault();
    if (!activeGroupId) {
      setError("Select a group first");
      return;
    }
    setError("");
    setOk("");
    setBusy(true);
    try {
      const contactIds =
        launchScope === "selected"
          ? Object.entries(selected)
              .filter(([, v]) => v)
              .map(([id]) => id)
          : undefined;
      const camp = await api<Campaign>("/bulk/campaigns", {
        method: "POST",
        body: JSON.stringify({
          groupId: activeGroupId,
          name: launchName || undefined,
          mode: launchMode,
          extension: launchExt,
          promptId: launchMode === "prerecorded" ? launchPromptId : undefined,
          delaySec: launchDelay,
          autoAnswer: launchAuto,
          selection: launchScope,
          contactIds,
          start: true,
        }),
      });
      setOk(`Campaign started · ${camp.total} calls queued`);
      setTab("campaigns");
      await refreshCampaigns();
      const detail = await api<CampaignDetail>(`/bulk/campaigns/${camp.id}`);
      setActiveCampaign(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start campaign");
    } finally {
      setBusy(false);
    }
  }

  async function campaignAction(id: string, action: "pause" | "resume" | "cancel") {
    setBusy(true);
    try {
      await api(`/bulk/campaigns/${id}/${action}`, { method: "POST" });
      await refreshCampaigns();
      if (activeCampaign?.id === id) {
        setActiveCampaign(await api<CampaignDetail>(`/bulk/campaigns/${id}`));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  const activeGroup = groups.find((g) => g.id === activeGroupId);
  const detailHasExtras = Boolean(
    activeCampaign?.items?.some((it) => it.renderedScript || it.errorMessage),
  );

  const liveCampaign =
    (activeCampaign && ["running", "paused"].includes(activeCampaign.status)
      ? activeCampaign
      : null) ||
    campaigns.find((c) => c.status === "running") ||
    campaigns.find((c) => c.status === "paused") ||
    null;

  const currentTarget = useMemo(() => {
    const items = activeCampaign?.items;
    if (!items?.length) return null;
    const dialing = items.find((it) => it.status === "dialing");
    if (dialing) return dialing;
    const inFlight = items
      .filter((it) => ["dialed", "answered"].includes(it.status) && !it.endedAt)
      .sort((a, b) => {
        const ta = a.dialedAt ? new Date(a.dialedAt).getTime() : 0;
        const tb = b.dialedAt ? new Date(b.dialedAt).getTime() : 0;
        return tb - ta;
      });
    if (inFlight[0]) return inFlight[0];
    if (liveCampaign && ["running", "paused"].includes(String(liveCampaign.status))) {
      return items.find((it) => it.status === "queued") || null;
    }
    return null;
  }, [activeCampaign?.items, liveCampaign]);

  function formatPhoneDisplay(phone: string) {
    const digits = String(phone || "").replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("0")) {
      return `${digits.slice(0, 5)} ${digits.slice(5)}`;
    }
    if (digits.length === 10) {
      return `${digits.slice(0, 5)} ${digits.slice(5)}`;
    }
    return phone || "—";
  }

  const progressPct = liveCampaign
    ? Math.min(
        100,
        Math.round(
          (((Number(liveCampaign.dialed) || 0) + (Number(liveCampaign.failed) || 0)) /
            Math.max(1, Number(liveCampaign.total) || 1)) *
            100,
        ),
      )
    : 0;

  return (
    <div className="bulk-page">
      <div className="ext-page-head">
        <div>
          <h1>Bulk calling</h1>
          <p className="muted">Groups · auto-answer campaigns · voice prompts</p>
        </div>
        <div className="ext-page-tools">
          <button className="btn ghost" type="button" disabled={busy} onClick={() => void loadAll()}>
            {busy ? "Loading…" : "Refresh"}
          </button>
          {tab === "groups" ? (
            <button
              className="btn"
              type="button"
              onClick={() => {
                setGroupName("");
                setModalError("");
                setGroupModalOpen(true);
              }}
            >
              Add group
            </button>
          ) : null}
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {ok ? <p className="ok">{ok}</p> : null}

      <div className="bulk-tabs" role="tablist">
        {(
          [
            ["groups", "Groups & numbers"],
            ["prompts", "Voice prompts"],
            ["launch", "Launch campaign"],
            ["campaigns", "Campaigns"],
          ] as Array<[Tab, string]>
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            className={`bulk-tab${tab === id ? " active" : ""}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bulk-body">
      {tab === "groups" ? (
        <section className="panel ext-panel bulk-fill-panel">
          <div className="panel-head bulk-groups-head">
            <div>
              <h3>Call groups</h3>
              <span className="muted">{groups.length} group{groups.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="bulk-table-scroll">
            <table className="table ext-table">
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Numbers</th>
                  <th className="ext-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.id} className={g.id === activeGroupId ? "bulk-row-active" : undefined}>
                    <td>
                      <b>{g.name}</b>
                    </td>
                    <td>{g.contactCount}</td>
                    <td className="ext-col-actions">
                      <div className="row-actions">
                        <button
                          className="btn ghost btn-tiny"
                          type="button"
                          onClick={() => void openNumbersModal(g.id)}
                        >
                          Add numbers
                        </button>
                        <button
                          className="btn ghost btn-tiny"
                          type="button"
                          onClick={() => void deleteGroup(g.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {groups.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="muted">
                      No groups yet — click Add group to create one.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {groupModalOpen ? (
        <div className="modal-back" onClick={() => setGroupModalOpen(false)}>
          <div className="modal user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>Add group</h2>
                <p className="muted">Create a list for bulk calling.</p>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={() => setGroupModalOpen(false)}
              >
                ×
              </button>
            </div>
            <form className="modal-form" onSubmit={(e) => void createGroup(e)}>
              <div className="modal-body">
                <label className="field">
                  <span>Group name</span>
                  <input
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="e.g. Debtors"
                    required
                    autoFocus
                  />
                </label>
                {modalError ? <p className="error">{modalError}</p> : null}
              </div>
              <div className="btn-row">
                <button className="btn" type="submit" disabled={busy}>
                  {busy ? "Saving…" : "Create group"}
                </button>
                <button className="btn ghost" type="button" onClick={() => setGroupModalOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {numbersModalOpen && activeGroup ? (
        <div
          className="modal-back"
          onClick={() => {
            setNumbersModalOpen(false);
            setModalError("");
            setModalOk("");
          }}
        >
          <div className="modal user-modal bulk-numbers-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>{activeGroup.name}</h2>
                <p className="muted">
                  Add numbers one by one or import a list · {contacts.length} in group
                  {selectedCount ? ` · ${selectedCount} selected` : ""}
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={() => {
                  setNumbersModalOpen(false);
                  setModalError("");
                  setModalOk("");
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body bulk-modal-body">
              <form className="bulk-modal-add" onSubmit={(e) => void addOneContact(e)}>
                <label className="field">
                  <span>Name</span>
                  <input
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Contact name"
                  />
                </label>
                <label className="field">
                  <span>Phone</span>
                  <input
                    value={contactPhone}
                    onChange={(e) => setContactPhone(localNumberInput(e.target.value))}
                    placeholder="10 digits"
                    required
                  />
                </label>
                <button className="btn" type="submit" disabled={busy}>
                  Add
                </button>
              </form>

              <div className="bulk-modal-import">
                <div className="bulk-import-toggle">
                  <span className="bulk-section-label">Bulk import</span>
                  <button
                    className="btn ghost"
                    type="button"
                    onClick={() => setShowImport((v) => !v)}
                  >
                    {showImport ? "Hide" : "Import"}
                  </button>
                </div>
                {showImport ? (
                  <div className="bulk-import-box">
                    <textarea
                      rows={4}
                      value={pasteText}
                      onChange={(e) => setPasteText(e.target.value)}
                      placeholder={"Name, Phone\nRahul, 9876543210\nPriya, 9123456780"}
                    />
                    <div className="btn-row">
                      <button
                        className="btn"
                        type="button"
                        disabled={busy || !pasteText.trim()}
                        onClick={() => void importPaste()}
                      >
                        Import numbers
                      </button>
                      <span className="muted">One per line · Name, Phone</span>
                    </div>
                  </div>
                ) : null}
              </div>

              {modalError ? <p className="error">{modalError}</p> : null}
              {modalOk ? <p className="ok">{modalOk}</p> : null}

              <div className="bulk-table-wrap bulk-modal-table">
                <table className="table ext-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={contacts.length > 0 && selectedCount === contacts.length}
                          onChange={(e) => {
                            const next: Record<string, boolean> = {};
                            if (e.target.checked) contacts.forEach((c) => (next[c.id] = true));
                            setSelected(next);
                          }}
                          aria-label="Select all"
                        />
                      </th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th className="ext-col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={Boolean(selected[c.id])}
                            onChange={(e) =>
                              setSelected((s) => ({ ...s, [c.id]: e.target.checked }))
                            }
                          />
                        </td>
                        <td>{c.name || "—"}</td>
                        <td>{c.phone}</td>
                        <td className="ext-col-actions">
                          <button
                            className="btn ghost btn-tiny"
                            type="button"
                            onClick={() => void removeContact(c.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                    {contacts.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="muted">
                          No numbers yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-foot">
              <button
                className="btn"
                type="button"
                onClick={() => {
                  setNumbersModalOpen(false);
                  setModalError("");
                  setModalOk("");
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {tab === "prompts" ? (
        <section className="panel ext-panel bulk-fill-panel">
          <div className="panel-head bulk-groups-head">
            <div>
              <h3>Voice prompts</h3>
              <span className="muted">Pre-recorded files and scripts with {"{{variables}}"}</span>
            </div>
          </div>
          <div className="bulk-split-full">
            <form className="bulk-form bulk-form-full" onSubmit={createPrompt}>
              <div className="bulk-form-grid">
                <label className="field">
                  <span>Name</span>
                  <input
                    value={promptName}
                    onChange={(e) => setPromptName(e.target.value)}
                    required
                    placeholder="Welcome notice"
                  />
                </label>
                <label className="field">
                  <span>Type</span>
                  <select value={promptKind} onChange={(e) => setPromptKind(e.target.value as Prompt["kind"])}>
                    <option value="file">Pre-recorded PBX file</option>
                    <option value="url">Audio URL</option>
                    <option value="script">Script / TTS text</option>
                  </select>
                </label>
                {promptKind !== "script" ? (
                  <label className="field">
                    <span>{promptKind === "url" ? "Audio URL" : "PBX playfile"}</span>
                    <input
                      value={promptFile}
                      onChange={(e) => setPromptFile(e.target.value)}
                      placeholder={promptKind === "url" ? "https://…" : "welcome.wav"}
                      required
                    />
                  </label>
                ) : null}
              </div>
              <label className="field">
                <span>Script template</span>
                <textarea
                  rows={3}
                  value={promptScript}
                  onChange={(e) => setPromptScript(e.target.value)}
                  placeholder="Hello {{name}}…"
                />
              </label>
              <div className="btn-row">
                <button className="btn ghost" type="button" onClick={() => void previewPrompt()}>
                  Preview
                </button>
                <button className="btn" type="submit" disabled={busy}>
                  Save prompt
                </button>
              </div>
              {promptPreview ? (
                <p className="bulk-preview">
                  <b>Preview:</b> {promptPreview}
                </p>
              ) : null}
            </form>
            <div className="bulk-table-scroll">
              <table className="table ext-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Content</th>
                    <th className="ext-col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {prompts.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <b>{p.name}</b>
                      </td>
                      <td>
                        <span className="pill">{p.kind}</span>
                      </td>
                      <td className="muted">{p.fileRef || p.scriptTemplate || "—"}</td>
                      <td className="ext-col-actions">
                        <button
                          className="btn ghost btn-tiny"
                          type="button"
                          onClick={() =>
                            void api(`/bulk/prompts/${p.id}`, { method: "DELETE" })
                              .then(refreshPrompts)
                              .catch((err) =>
                                setError(err instanceof Error ? err.message : "Delete failed"),
                              )
                          }
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {prompts.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="muted">
                        No prompts saved yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {tab === "launch" ? (
        <section className="panel ext-panel bulk-fill-panel">
          <div className="panel-head">
            <h3>Start bulk campaign</h3>
          </div>
          <form className="bulk-form bulk-form-full bulk-launch-full" onSubmit={launchCampaign}>
            <div className="bulk-form-grid">
              <label className="field">
                <span>Campaign name</span>
                <input
                  value={launchName}
                  onChange={(e) => setLaunchName(e.target.value)}
                  placeholder="Optional"
                />
              </label>
              <label className="field">
                <span>Group</span>
                <select
                  value={activeGroupId}
                  onChange={(e) => setActiveGroupId(e.target.value)}
                  required
                >
                  <option value="">Select group…</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} ({g.contactCount})
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Extension</span>
                <ExtensionSelect
                  value={launchExt}
                  onChange={setLaunchExt}
                  extensions={extensions}
                  allowEmpty={false}
                  required
                  exclusive={false}
                />
              </label>
              <label className="field">
                <span>Max ring/talk time (sec)</span>
                <input
                  type="number"
                  min={20}
                  max={300}
                  value={launchDelay}
                  onChange={(e) => setLaunchDelay(Number(e.target.value) || 45)}
                />
              </label>
            </div>

            <div className="bulk-launch-options">
              <fieldset className="bulk-fieldset">
                <legend>Who to dial</legend>
                <label className="bulk-choice">
                  <input
                    type="radio"
                    checked={launchScope === "all"}
                    onChange={() => setLaunchScope("all")}
                  />
                  <span>
                    <b>All numbers in group</b>
                    <em>{activeGroup ? `${activeGroup.contactCount} contacts` : "Select a group"}</em>
                  </span>
                </label>
                <label className="bulk-choice">
                  <input
                    type="radio"
                    checked={launchScope === "selected"}
                    onChange={() => setLaunchScope("selected")}
                  />
                  <span>
                    <b>Selected numbers only</b>
                    <em>
                      {selectedCount
                        ? `${selectedCount} selected`
                        : "Open Add numbers and tick rows first"}
                    </em>
                  </span>
                </label>
              </fieldset>
              <fieldset className="bulk-fieldset">
                <legend>Call mode</legend>
                <label className="bulk-choice">
                  <input
                    type="radio"
                    checked={launchMode === "live"}
                    onChange={() => setLaunchMode("live")}
                  />
                  <span>
                    <b>Live agent (auto click-to-call)</b>
                    <em>Extension auto-answers, then dials the customer</em>
                  </span>
                </label>
                <label className="bulk-choice">
                  <input
                    type="radio"
                    checked={launchMode === "prerecorded"}
                    onChange={() => setLaunchMode("prerecorded")}
                  />
                  <span>
                    <b>Pre-recorded / script</b>
                    <em>Auto-answer dial and play prompt with variables</em>
                  </span>
                </label>
              </fieldset>
            </div>

            {launchMode === "prerecorded" ? (
              <label className="field">
                <span>Voice prompt</span>
                <select
                  value={launchPromptId}
                  onChange={(e) => setLaunchPromptId(e.target.value)}
                  required
                >
                  <option value="">Select prompt…</option>
                  {prompts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.kind})
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <label className="bulk-inline-check">
              <input
                type="checkbox"
                checked={launchAuto}
                onChange={(e) => setLaunchAuto(e.target.checked)}
              />
              Enable auto-answer click
            </label>

            <div className="btn-row">
              <button className="btn" type="submit" disabled={busy}>
                {busy ? "Starting…" : "Start bulk calling"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {tab === "campaigns" ? (
        <section className="panel ext-panel bulk-fill-panel">
          <div className="panel-head bulk-groups-head">
            <div>
              <h3>Campaigns</h3>
              <span className="muted">{campaigns.length} recent</span>
            </div>
          </div>
          <div className="bulk-campaigns-layout">
            <div className="bulk-campaigns-main">
              <div className="bulk-table-scroll">
                <table className="table ext-table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Mode</th>
                      <th>Ext</th>
                      <th>Status</th>
                      <th>Dialed</th>
                      <th>Failed</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((c) => (
                      <tr
                        key={c.id}
                        className={activeCampaign?.id === c.id ? "bulk-row-active" : undefined}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          void api<CampaignDetail>(`/bulk/campaigns/${c.id}`).then(setActiveCampaign)
                        }
                      >
                        <td>
                          <b>{c.name}</b>
                        </td>
                        <td>{c.mode}</td>
                        <td>{c.extension}</td>
                        <td>
                          <span className={`pill status-${c.status}`}>{c.status}</span>
                        </td>
                        <td>{c.dialed}</td>
                        <td>{c.failed}</td>
                        <td>{c.total}</td>
                      </tr>
                    ))}
                    {campaigns.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="muted">
                          No campaigns yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              {activeCampaign ? (
                <div className="bulk-table-scroll bulk-detail-scroll">
                  <table className="table ext-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Status</th>
                        {detailHasExtras ? <th>Note</th> : null}
                        {detailHasExtras ? <th>Error</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {activeCampaign.items.map((it) => (
                        <tr
                          key={it.id}
                          className={
                            currentTarget?.id === it.id ? "bulk-row-targeting" : undefined
                          }
                        >
                          <td>{it.displayName || "—"}</td>
                          <td>{it.phone}</td>
                          <td>
                            <span className={`pill status-${it.status}`}>{it.status}</span>
                          </td>
                          {detailHasExtras ? (
                            <td className="muted">{it.renderedScript || "—"}</td>
                          ) : null}
                          {detailHasExtras ? (
                            <td className="muted">{it.errorMessage || "—"}</td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>

            <aside className="bulk-live-card" aria-live="polite">
              {liveCampaign && ["running", "paused"].includes(String(liveCampaign.status)) ? (
                <>
                  <div className="bulk-live-card-top">
                    <span
                      className={`bulk-live-pulse ${liveCampaign.status === "paused" ? "is-paused" : ""}`}
                    />
                    <div>
                      <p className="bulk-live-eyebrow">
                        {liveCampaign.status === "running" ? "Running now" : "Paused"}
                      </p>
                      <h4>{liveCampaign.name}</h4>
                    </div>
                    <span className={`pill status-${liveCampaign.status}`}>
                      {liveCampaign.status}
                    </span>
                  </div>

                  <div className="bulk-live-target">
                    <p className="bulk-live-eyebrow">Targeting</p>
                    {currentTarget ? (
                      <>
                        <p className="bulk-live-name">
                          {currentTarget.displayName || "Unknown contact"}
                        </p>
                        <p className="bulk-live-phone">
                          {formatPhoneDisplay(currentTarget.phone)}
                        </p>
                        <span className={`pill status-${currentTarget.status}`}>
                          {currentTarget.status === "queued"
                            ? "Next up"
                            : currentTarget.status === "dialing"
                              ? "Dialing…"
                              : currentTarget.status === "dialed" && !currentTarget.endedAt
                                ? "On the line"
                                : currentTarget.status}
                        </span>
                      </>
                    ) : (
                      <p className="muted">Waiting for the next number…</p>
                    )}
                  </div>

                  <div className="bulk-live-meter" aria-hidden>
                    <i style={{ width: `${progressPct}%` }} />
                  </div>
                  <div className="bulk-live-stats">
                    <div>
                      <span>Dialed</span>
                      <b>{liveCampaign.dialed}</b>
                    </div>
                    <div>
                      <span>Queued</span>
                      <b>{liveCampaign.queued}</b>
                    </div>
                    <div>
                      <span>Failed</span>
                      <b>{liveCampaign.failed}</b>
                    </div>
                    <div>
                      <span>Total</span>
                      <b>{liveCampaign.total}</b>
                    </div>
                  </div>
                  <p className="bulk-live-meta muted">
                    Ext {liveCampaign.extension} · {liveCampaign.mode}
                    {liveCampaign.mode === "live" ? " · stays up after answer" : ""}
                  </p>
                  <div className="btn-row bulk-live-actions">
                    {liveCampaign.status === "running" ? (
                      <button
                        className="btn ghost"
                        type="button"
                        onClick={() => void campaignAction(String(liveCampaign.id), "pause")}
                      >
                        Pause
                      </button>
                    ) : (
                      <button
                        className="btn"
                        type="button"
                        onClick={() => void campaignAction(String(liveCampaign.id), "resume")}
                      >
                        Resume
                      </button>
                    )}
                    <button
                      className="btn ghost"
                      type="button"
                      onClick={() => void campaignAction(String(liveCampaign.id), "cancel")}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="bulk-live-empty">
                  <p className="bulk-live-eyebrow">Live campaign</p>
                  <h4>No campaign running</h4>
                  <p className="muted">
                    Start a campaign from Launch — the person being dialed will show here with their
                    number.
                  </p>
                  <button className="btn" type="button" onClick={() => setTab("launch")}>
                    Launch campaign
                  </button>
                </div>
              )}
            </aside>
          </div>
        </section>
      ) : null}
      </div>
    </div>
  );
}
